import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import FormBuilder from '../../../form-builder/FormBuilder';
import { useDataStore } from '../../../../../hooks/useDataStore';
import { MarsApiConfig as apiConfig } from '../../../../../api/mars-api-config';

export default function DistanceCalculatorForm() {
  const { t } = useTranslation();
  const { control, resetField, trigger, setValue } = useFormContext();

  const { getDataSource } = useDataStore();
  const searchExpression = ['port', 'countryName'];
  const portDataSource = getDataSource('portId', apiConfig.activePorts);
  const viaDataSource = getDataSource('code', apiConfig.availableGateways);

  const coordinateToggleOptions = [
    { id: false, value: t('labels.usePort') },
    { id: true, value: t('labels.useCoordinates') },
  ];

  const [useFromCoordinates, setUseFromCoordinates] = useState(false);
  const [useToCoordinates, setUseToCoordinates] = useState(false);

  const valueChanged = (e: any, isFromToggle: boolean) => {
    const newValue = e.value;
    if (isFromToggle) {
      setValue('useFromCoordinates', newValue, { shouldValidate: true });
      setUseFromCoordinates(newValue);
      if (newValue) {
        resetField('fromPort', { defaultValue: null });
      } else {
        resetField('fromLat', { defaultValue: null });
        resetField('fromLon', { defaultValue: null });
      }
    } else {
      setValue('useToCoordinates', newValue, { shouldValidate: true });
      setUseToCoordinates(newValue);
      if (newValue) {
        resetField('toPort', { defaultValue: null });
      } else {
        resetField('toLat', { defaultValue: null });
        resetField('toLon', { defaultValue: null });
      }
    }
    trigger();
  };

  const onOpened = (e: any) => {
    e.component.content().querySelector('.dx-list-select-all').style.display =
      'none';
  };

  useEffect(() => {
    const currentFromValue = control._formValues.useFromCoordinates ?? false;
    const currentToValue = control._formValues.useToCoordinates ?? false;
    if (currentFromValue !== useFromCoordinates) {
      setUseFromCoordinates(currentFromValue);
    }
    if (currentToValue !== useToCoordinates) {
      setUseToCoordinates(currentToValue);
    }
  }, [control, useFromCoordinates, useToCoordinates]);

  const baseFormConfig = [
    {
      name: 'gateways',
      label: t('labels.via'),
      displayExpr: 'description',
      valueExpr: 'code',
      control: control,
      fieldType: 'multiSelect',
      isRequired: false,
      dataSource: viaDataSource,
      onOpened: onOpened,
      class: 'col-xl-3 col-lg-3 col-md-12 col-sm-12 col-12',
    },
    {
      name: 'speed',
      label: t('labels.speedKnots'),
      control: control,
      fieldType: 'number',
      allowDecimal: true,
      class: 'col-xl-2 col-lg-2 col-md-12 col-sm-12 col-12 custom-xl-2',
      rules: [
        {
          type: 'range',
          message: t('errors.invalidSpeed'),
          min: 1,
        },
      ],
    },
    {
      name: 'startAt',
      label: t('labels.startAt'),
      control: control,
      fieldType: 'datetime',
      class:
        'col-xl-2 col-lg-2 col-md-12 col-sm-12 col-12 m-l-custom-eight-space-col',
    },
  ];

  const fromPortConfig = [
    {
      name: 'fromPort',
      label: t('labels.fromPort'),
      control: control,
      fieldType: 'select',
      isRequired: true,
      displayExpr: 'portName',
      valueExpr: 'portId',
      searchExpr: searchExpression,
      dataSource: portDataSource,
      class:
        'col-xl-6 col-lg-6 col-md-12 col-sm-12 col-12 m-l-custom-eight-space-col',
      rules: [
        {
          type: 'required',
          message: t('errors.required'),
        },
      ],
    },
  ];

  const toPortConfig = [
    {
      name: 'toPort',
      label: t('labels.toPort'),
      control: control,
      fieldType: 'select',
      isRequired: true,
      displayExpr: 'portName',
      valueExpr: 'portId',
      searchExpr: searchExpression,
      dataSource: portDataSource,
      class:
        'col-xl-6 col-lg-6 col-md-12 col-sm-12 col-12 m-l-custom-eight-space-col',
      rules: [
        {
          type: 'required',
          message: t('errors.required'),
        },
      ],
    },
  ];

  const fromCoordinateConfig = [
    {
      name: 'fromLat',
      label: t('labels.fromLatitude'),
      control: control,
      fieldType: 'number',
      allowDecimal: true,
      isRequired: true,
      class: 'col-xl-3 col-lg-3 col-md-12 col-sm-12 col-12 ',
      rules: [
        {
          type: 'required',
          message: t('errors.required'),
        },
      ],
    },
    {
      name: 'fromLon',
      label: t('labels.fromLongitude'),
      control: control,
      fieldType: 'number',
      allowDecimal: true,
      isRequired: true,
      class: 'col-xl-3 col-lg-3 col-md-12 col-sm-12 col-12',
      rules: [
        {
          type: 'required',
          message: t('errors.required'),
        },
      ],
    },
  ];

  const toCoordinateConfig = [
    {
      name: 'toLat',
      label: t('labels.toLatitude'),
      control: control,
      fieldType: 'number',
      allowDecimal: true,
      isRequired: true,
      class:
        'ps-2 col-xl-3 col-lg-3 col-md-12 col-sm-12 col-12 m-l-custom-eight-space-col',
      rules: [
        {
          type: 'required',
          message: t('errors.required'),
        },
      ],
    },
    {
      name: 'toLon',
      label: t('labels.toLongitude'),
      control: control,
      fieldType: 'number',
      allowDecimal: true,
      isRequired: true,
      class:
        'col-xl-3 col-lg-3 col-md-12 col-sm-12 col-12 m-l-custom-eight-space-col',
      rules: [
        {
          type: 'required',
          message: t('errors.required'),
        },
      ],
    },
  ];

  const toggleFormConfig = [
    {
      name: 'useFromCoordinates',
      label: t('labels.fromLocationType'),
      control: control,
      fieldType: 'radio',
      layout: 'horizontal',
      items: coordinateToggleOptions,
      displayExpr: 'value',
      valueExpr: 'id',
      fieldWrapperClass: 'm-c-form-group mb-btm-space-20 tp-space-det',
      fieldClass: 'm-c-radio-button m-c-custom-space-radiobtn',
      onChange: (e: any) => valueChanged(e, true),
      class:
        'col-xl-3 col-lg-3 col-md-12 col-sm-12 col-12 m-l-custom-eight-space-col',
    },
    {
      name: 'useToCoordinates',
      label: t('labels.toLocationType'),
      control: control,
      fieldType: 'radio',
      layout: 'horizontal',
      items: coordinateToggleOptions,
      displayExpr: 'value',
      valueExpr: 'id',
      fieldWrapperClass: 'm-c-form-group mb-btm-space-20 tp-space-det',
      fieldClass: 'm-c-radio-button m-c-custom-space-radiobtn',
      onChange: (e: any) => valueChanged(e, false),
      class:
        'col-xl-4 col-lg-4 col-md-12 col-sm-12 col-12 m-l-custom-eight-space-col',
    },
  ];

  return (
    <div className="m-l-distance-calculator-form">
      <div className="row m-l-toggle-row">
        <FormBuilder formConfig={toggleFormConfig} />
      </div>
      <div className="flex flex-wrap">
        <div className="row">
          <div className="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-12 row">
            <FormBuilder
              formConfig={
                useFromCoordinates ? fromCoordinateConfig : fromPortConfig
              }
            />
            <FormBuilder
              formConfig={useToCoordinates ? toCoordinateConfig : toPortConfig}
            />
          </div>
          <FormBuilder formConfig={baseFormConfig} />
        </div>
      </div>
    </div>
  );
}
